import React from "react";
import { useSelector } from "react-redux";
import {
  Actionable,
  AspectRatio,
  Avatar,
  AvatarProps,
  Carousel,
  Image,
  Stack,
  Text,
  useViewport
} from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { getFlyAnywhereCountries } from "store/flyAnywhere/selectors";
import styles from "./FlyAnywhereCountriesCarousel.module.css";
import { FlyAnywhereCountry } from "@flights/types";
import useCreateUrl from "hooks/useCreateUrl";

const FlyAnywhereCountriesCarousel = () => {
  const i18n = useI18n();
  const countries = useSelector(getFlyAnywhereCountries);
  const { isSmall } = useViewport();
  const { createUrlWithApiPrefix } = useCreateUrl();

  if (!countries?.length) {
    return null;
  }

  const getCardUrl = (countryCode: string) => {
    return createUrlWithApiPrefix(`/fly-anywhere?toCountryCode=${countryCode}`);
  };

  const itemsToRender: (FlyAnywhereCountry | { isSpecialCard: true })[] = [...countries];

  // Place the special card at different positions based on viewport size
  if (itemsToRender.length > 0) {
    let specialCardIndex = 3;

    if (isSmall) {
      specialCardIndex = 1;
    }

    if (specialCardIndex < itemsToRender.length) {
      itemsToRender.splice(specialCardIndex, 0, { isSpecialCard: true });
    } else {
      itemsToRender.push({ isSpecialCard: true });
    }
  } else {
    itemsToRender.push({ isSpecialCard: true });
  }

  return (
    <>
      <Text variant="headline_2">{i18n.trans(t("flights_explore_by_country_header"))}</Text>
      <Text variant="body_1" color="neutral_alt">
        {i18n.trans(t("flights_explore_by_country_subheader"))}
      </Text>
      <Carousel
        size={isSmall ? "large" : "medium"}
        nextButtonAriaLabel={i18n.trans(t("a11y_flights_route_carousel_next_button_aria_label"))}
        previousButtonAriaLabel={i18n.trans(t("a11y_flights_route_carousel_previous_button_aria_label"))}
        className={styles.carousel}
      >
        {itemsToRender.map((item) => {
          if ("isSpecialCard" in item) {
            return <SpecialCard key="special-card" />;
          }

          const { code, imgSlug, translatedCountryName } = item;
          return (
            <Actionable key={code} className={styles.card} href={getCardUrl(code)}>
              <AspectRatio ratio={isSmall ? "5:4" : "4:5"}>
                <div className={styles.imageWrapper}>
                  <Image src={imgSlug} borderRadius={200} className={styles.image} />
                  <div className={styles.imageOverlay}></div>
                  <Stack className={styles.textContent} direction="row">
                    <Avatar countryCode={code as AvatarProps["countryCode"]} size="small" className={styles.flag} />
                    <Text variant="headline_3" color="white" className={styles.countryName}>
                      {translatedCountryName}
                    </Text>
                  </Stack>
                </div>
              </AspectRatio>
            </Actionable>
          );
        })}
      </Carousel>
    </>
  );
};

const SpecialCard = () => {
  const i18n = useI18n();
  const { isSmall } = useViewport();
  const { createUrlWithApiPrefix } = useCreateUrl();

  return (
    <Actionable className={styles.card} href={createUrlWithApiPrefix("/fly-anywhere")}>
      <AspectRatio ratio={isSmall ? "5:4" : "4:5"}>
        <Stack className={styles.specialCard}>
          <Image
            width="64px"
            height="64px"
            asset={{
              setName: "illustrations-traveller",
              assetName: "TripsGlobe"
            }}
            contentMode="fit"
          />
          <Text variant="headline_3" color="white">
            {i18n.trans(t("flights_explore_by_country_card_anywhere_title"))}
          </Text>
          <Text variant="body_1" color="white">
            {i18n.trans(t("flights_explore_by_country_card_anywhere_subtitle"))}
          </Text>
        </Stack>
      </AspectRatio>
    </Actionable>
  );
};

export default FlyAnywhereCountriesCarousel;
